/**
 * MidArticleCard - compact inline related post recommendation
 * Design: Signal & Authority, Editorial Brutalism meets Swiss Typography
 *
 * Injected after the third H2 heading in long-form articles to reduce bounce rate.
 * Styled as an editorial aside: left accent bar, thumbnail, title, reading time.
 * Deliberately restrained - feels like a "you might also like" editorial note,
 * not a banner ad. Uses <a> (not JS navigation) for SEO link equity.
 *
 * Accessibility: role="complementary" + aria-label so screen readers announce it
 * as a related content block rather than inline article text.
 */

import { Clock, ArrowRight } from "lucide-react";
import { Link } from "wouter";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { trackRelatedPostClick } from "@/lib/analytics";

interface MidArticleCardProps {
  fromSlug: string;
  slug: string;
  title: string;
  excerpt: string;
  heroImage: string;
  date: string;
  tags: string[];
  content: string;
  /** Tag shared with the current article - shown as the "why" signal */
  sharedTag?: string;
}

function readingTime(content: string) {
  return Math.max(1, Math.ceil(content.split(/\s+/).length / 200));
}

export default function MidArticleCard({
  fromSlug,
  slug,
  title,
  excerpt,
  heroImage,
  date,
  tags,
  content,
  sharedTag,
}: MidArticleCardProps) {
  const mins = readingTime(content);
  const displayTag = sharedTag ?? tags[0] ?? null;

  return (
    <aside
      role="complementary"
      aria-label="Related article recommendation"
      className="not-prose my-10 mx-auto"
    >
      <Link href={`/blog/${slug}/`}>
        <article
          data-track="related-post-click"
          data-to={slug}
          data-position={1}
          data-placement="mid-article"
          onClick={() => trackRelatedPostClick({
            from_slug: fromSlug,
            to_slug: slug,
            position: 1,
            placement: "mid-article",
          })}
          className="group flex gap-0 rounded-2xl border border-border bg-card overflow-hidden
            hover:border-amber-500/50 hover:shadow-lg hover:shadow-amber-500/8
            transition-all duration-300 ease-out cursor-pointer"
          aria-label={`Read related article: ${title}`}
        >
          {/* Left amber accent bar */}
          <div className="w-1 shrink-0 bg-amber-500/60 group-hover:bg-amber-500 transition-colors duration-300" />

          {/* Thumbnail - hidden on very small screens */}
          <div className="hidden sm:block w-32 md:w-40 shrink-0 overflow-hidden bg-muted">
            <ResponsiveImage
              src={heroImage}
              alt={title}
              width={160}
              height={120}
              sizes="160px"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>

          {/* Body */}
          <div className="flex flex-col justify-center flex-1 px-5 py-4 min-w-0">
            {/* "Keep reading" label + tag */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Keep reading
              </span>
              {displayTag && (
                <>
                  <span className="text-muted-foreground/40 text-xs" aria-hidden="true">·</span>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 border border-amber-500/20 uppercase tracking-wide">
                    {displayTag}
                  </span>
                </>
              )}
            </div>

            {/* Title */}
            <h4 className="font-serif text-base md:text-lg leading-snug text-foreground
              group-hover:text-amber-600 transition-colors duration-200 line-clamp-2 mb-1.5">
              {title}
            </h4>

            {/* Excerpt - single line */}
            <p className="text-sm text-muted-foreground line-clamp-1 mb-3">
              {excerpt}
            </p>

            {/* Meta + CTA */}
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3 shrink-0" />
                {mins} min read
              </span>
              <span className="ml-auto inline-flex items-center gap-1 font-semibold text-amber-600
                opacity-0 group-hover:opacity-100 translate-x-[-4px] group-hover:translate-x-0
                transition-all duration-200">
                Read now <ArrowRight className="w-3 h-3" />
              </span>
            </div>
          </div>
        </article>
      </Link>
    </aside>
  );
}
